import React from 'react';
import connectToStores from 'alt-utils/lib/connectToStores';
import LibraryStore from 'stores/library/LibraryStore';
import GenericTooltip from 'components/pure/tooltips/GenericTooltip';
import moment from 'moment';
import {browserHistory} from 'react-router';
import GenericLink from 'components/GenericLink';
import './LibraryTagDisplay.styl';
import groupBy from 'lodash/groupBy';

import {LIBRARY_ROUTE} from '@store/library/library.consts';
import {TOOLTIP_DELAY} from 'components/StatsPage2.utils';
import {TrashIcon} from '@components/icon/TrashIcon';
import {FileUploadId} from 'records/CoachingReviewFileUpload';
import FileUploadStore, {TagData} from 'stores/files/FileUploadStore';
import {List} from 'immutable';
import {LibraryId} from '@store/@types/records/Library';
import LibraryTagDeleteDialog from 'components/pure/library/LibraryTagDeleteDialog';

const goToLibrary = libraryId => {
  browserHistory.push(LIBRARY_ROUTE.SEARCH_LIBRARY(libraryId));
};

interface LibraryTagDisplayProps {
  fileUploadId: FileUploadId;
  // optional - Filter tags to one library
  libraryId?: LibraryId;
}

interface LibraryTagDisplayState {
  displayAll: boolean;
  libraryTagToDelete: TagData | null;
}

/**
 * A component to display library tags associated with a call.
 *
 * Displayed on the call card.
 *
 * Provides popover & hooks to show more info, links into libraries/tags
 */
@connectToStores
class LibraryTagDisplay extends React.Component<
  LibraryTagDisplayProps & ReturnType<typeof LibraryTagDisplay.getPropsFromStores>,
  LibraryTagDisplayState
> {
  state = {displayAll: false, libraryTagToDelete: null};

  static getStores() {
    return [LibraryStore];
  }

  static getPropsFromStores(props: LibraryTagDisplayProps) {
    const tags = FileUploadStore.getState().callTags.get(props.fileUploadId, List()).toJS();
    return {
      libraries: LibraryStore.getState().getIn(['sources', 'libraries']),
      libraryTags: tags.filter(t =>
        props.libraryId ? t.library_id.toString() === props.libraryId : true
      ),
    };
  }

  /**
   * partition into [show, hide]
   *
   * @param tags - the library tags for this call
   * @return [[tagName, [{tag}, {tag}...]]] - grouped tags for the popover
   */
  partitionDisplayedTags(tags: TagData[]) {
    const maxCharacters = 25;
    let displayedCharacters = 0;
    const displayed: Array<[string, TagData[]]> = [];

    const maxTags = 3;

    const grouped = Object.entries(groupBy(tags, t => t.tag_name));

    for (const [name, tags] of grouped) {
      const charAmt = name.length;

      if (charAmt + displayedCharacters <= maxCharacters && displayed.length <= maxTags - 1) {
        displayedCharacters += charAmt;
        displayed.push([name, tags]);
      } else {
        break;
      }
    }

    return [displayed, grouped.filter(([name]) => !displayed.map(([name]) => name).includes(name))];
  }

  render() {
    const {libraryTags, libraries} = this.props;
    const {displayAll, libraryTagToDelete} = this.state;

    const [tagsToShow, tagsToHide] = this.partitionDisplayedTags(libraryTags);

    const visibleTags = [...tagsToShow, ...(displayAll ? tagsToHide : [])];

    return (
      <div className="lib-tag-container">
        {visibleTags.map(shown => (
          <TagWithPopover
            key={shown[0]}
            groupedTags={shown}
            libraries={libraries}
            onDelete={tag => this.setState({libraryTagToDelete: tag})}
          />
        ))}
        {!displayAll && tagsToHide.length > 0 && (
          <div className="more" onClick={() => this.setState({displayAll: true})}>
            {tagsToShow.length > 0 ? '+' + tagsToHide.length : tagsToHide.length + ' tags'}
          </div>
        )}
        <LibraryTagDeleteDialog
          libraryTagToDelete={libraryTagToDelete}
          onClose={() => this.setState({libraryTagToDelete: null})}
        />
      </div>
    );
  }
}

const TagWithPopover = ({groupedTags, libraries, onDelete, ...props}) => {
  if (!libraries) return null;

  const [tagName, tags] = groupedTags;

  return (
    <GenericTooltip
      hoverBehavior
      delay={TOOLTIP_DELAY}
      tooltip={
        <div className={'library-tag-popover'}>
          <div className="info">
            <p className="library-info">
              This call was tagged <span className="generic-link">{tagName}</span> by:
            </p>
            {tags.map(t => (
              <LibraryTagCombo
                key={t.library_id}
                tag={t}
                library={libraries.find(l => parseInt(l.id, 10) === t.library_id)}
                onDelete={() => onDelete(t)}
              />
            ))}
          </div>
        </div>
      }
      {...props}>
      <span className="generic-link">{tagName}</span>
    </GenericTooltip>
  );
};

const LibraryTagCombo = ({tag, library, onDelete}) => {
  if (!library || !tag) return null;

  return (
    <div className="library-tag-combo">
      <p>
        <span>
          <strong>{tag.tag_user_name}</strong> in{' '}
          <GenericLink onClick={() => goToLibrary(tag.library_id)}>{tag.library_name}</GenericLink>{' '}
          on <strong>{moment(tag.tag_created).format('M/DD/YY')}</strong>
        </span>
      </p>
      <div className="delete">
        {library && library.permissions.includes('remove') && (
          <TrashIcon onClick={onDelete} tooltip="Delete tag" />
        )}
      </div>
    </div>
  );
};

export default LibraryTagDisplay as unknown as React.FC<LibraryTagDisplayProps>;
